import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Rocket, ListTodo, Gift, CheckCircle2, ArrowRight, 
  Loader2, Sparkles, Link2, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InteractiveDemo({ onComplete }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [taskListName, setTaskListName] = useState('');
  const [rewardListName, setRewardListName] = useState('');
  const [createdTaskList, setCreatedTaskList] = useState(null);
  const [createdRewardList, setCreatedRewardList] = useState(null);

  const content = {
    es: {
      title: '¡Vamos a crear tu primera configuración!',
      subtitle: 'Sigue estos pasos para empezar a usar Motivator',
      steps: [
        {
          icon: ListTodo,
          color: '#FF8BA7',
          title: 'Paso 1: Crea tu lista de tareas',
          description: 'Dale un nombre a tu primera lista de tareas. Por ejemplo: "Tareas del hogar", "Trabajo" o "Ejercicio".',
          placeholder: 'Ej: Mis tareas diarias',
          buttonText: 'Crear lista de tareas',
          creating: 'Creando...'
        },
        {
          icon: Gift,
          color: '#FFC247',
          title: 'Paso 2: Crea tu lista de premios',
          description: 'Ahora crea una lista de premios asociada. Los puntos que ganes en tu lista de tareas se podrán canjear aquí.',
          placeholder: 'Ej: Mis recompensas',
          buttonText: 'Crear lista de premios',
          creating: 'Creando...'
        },
        {
          icon: Link2,
          color: '#ADE498',
          title: '¡Listas vinculadas!',
          description: 'Tu lista de premios está vinculada a tu lista de tareas. Los puntos que ganes completando tareas solo podrán canjearse en los premios de esta lista.',
          tip: 'Puedes crear múltiples listas de tareas, cada una con sus propias listas de premios.'
        },
        {
          icon: Trophy,
          color: '#3B82F6',
          title: '¡Todo listo!',
          description: 'Ya tienes tu primera configuración. Ahora puedes añadir tareas, completarlas para ganar puntos, y canjear premios.',
          buttonText: '¡Empezar a usar Motivator!',
          tip: 'Recuerda: puedes repetir el tutorial desde el botón de ayuda (?) en cualquier momento.'
        }
      ],
      skipDemo: 'Saltar demo',
      stepOf: 'de'
    },
    en: {
      title: "Let's create your first setup!",
      subtitle: 'Follow these steps to start using Motivator',
      steps: [
        {
          icon: ListTodo,
          color: '#FF8BA7',
          title: 'Step 1: Create your task list',
          description: 'Give your first task list a name. For example: "Home tasks", "Work" or "Exercise".',
          placeholder: 'E.g.: My daily tasks',
          buttonText: 'Create task list',
          creating: 'Creating...'
        },
        {
          icon: Gift,
          color: '#FFC247',
          title: 'Step 2: Create your reward list',
          description: 'Now create an associated reward list. Points earned in your task list can be redeemed here.',
          placeholder: 'E.g.: My rewards',
          buttonText: 'Create reward list',
          creating: 'Creating...'
        },
        {
          icon: Link2,
          color: '#ADE498',
          title: 'Lists linked!',
          description: 'Your reward list is linked to your task list. Points earned by completing tasks can only be redeemed for rewards in this list.',
          tip: 'You can create multiple task lists, each with their own reward lists.'
        },
        {
          icon: Trophy,
          color: '#3B82F6',
          title: 'All set!',
          description: 'You have your first setup ready. Now you can add tasks, complete them to earn points, and redeem rewards.',
          buttonText: 'Start using Motivator!',
          tip: 'Remember: you can repeat the tutorial from the help button (?) at any time.'
        }
      ],
      skipDemo: 'Skip demo',
      stepOf: 'of'
    },
    gl: {
      title: 'Imos crear a túa primeira configuración!',
      subtitle: 'Segue estes pasos para comezar a usar Motivator',
      steps: [
        {
          icon: ListTodo,
          color: '#FF8BA7',
          title: 'Paso 1: Crea a túa lista de tarefas',
          description: 'Dalle un nome á túa primeira lista de tarefas. Por exemplo: "Tarefas do fogar", "Traballo" ou "Exercicio".',
          placeholder: 'Ex: As miñas tarefas diarias',
          buttonText: 'Crear lista de tarefas',
          creating: 'Creando...'
        },
        {
          icon: Gift,
          color: '#FFC247',
          title: 'Paso 2: Crea a túa lista de premios',
          description: 'Agora crea unha lista de premios asociada. Os puntos que gañes na túa lista de tarefas poderanse canjear aquí.',
          placeholder: 'Ex: As miñas recompensas',
          buttonText: 'Crear lista de premios',
          creating: 'Creando...'
        },
        {
          icon: Link2,
          color: '#ADE498',
          title: 'Listas vinculadas!',
          description: 'A túa lista de premios está vinculada á túa lista de tarefas. Os puntos que gañes completando tarefas só poderán canjearse nos premios desta lista.',
          tip: 'Podes crear múltiples listas de tarefas, cada unha coas súas propias listas de premios.'
        },
        {
          icon: Trophy,
          color: '#3B82F6',
          title: 'Todo listo!',
          description: 'Xa tes a túa primeira configuración. Agora podes engadir tarefas, completalas para gañar puntos, e canjear premios.',
          buttonText: 'Comezar a usar Motivator!',
          tip: 'Lembra: podes repetir o tutorial desde o botón de axuda (?) en calquera momento.'
        }
      ],
      skipDemo: 'Saltar demo',
      stepOf: 'de'
    },
    ca: {
      title: 'Anem a crear la teva primera configuració!',
      subtitle: 'Segueix aquests passos per començar a usar Motivator',
      steps: [
        {
          icon: ListTodo,
          color: '#FF8BA7',
          title: 'Pas 1: Crea la teva llista de tasques',
          description: "Dona un nom a la teva primera llista de tasques. Per exemple: \"Tasques de la llar\", \"Treball\" o \"Exercici\".",
          placeholder: 'Ex: Les meves tasques diàries',
          buttonText: 'Crear llista de tasques',
          creating: 'Creant...'
        },
        {
          icon: Gift,
          color: '#FFC247',
          title: 'Pas 2: Crea la teva llista de premis',
          description: "Ara crea una llista de premis associada. Els punts que guanyis a la teva llista de tasques es podran bescanviar aquí.",
          placeholder: 'Ex: Les meves recompenses',
          buttonText: 'Crear llista de premis',
          creating: 'Creant...'
        },
        {
          icon: Link2,
          color: '#ADE498',
          title: 'Llistes vinculades!',
          description: "La teva llista de premis està vinculada a la teva llista de tasques. Els punts que guanyis completant tasques només es podran bescanviar pels premis d'aquesta llista.",
          tip: 'Pots crear múltiples llistes de tasques, cadascuna amb les seves pròpies llistes de premis.'
        },
        {
          icon: Trophy,
          color: '#3B82F6',
          title: 'Tot llest!',
          description: 'Ja tens la teva primera configuració. Ara pots afegir tasques, completar-les per guanyar punts, i bescanviar premis.',
          buttonText: 'Començar a usar Motivator!',
          tip: "Recorda: pots repetir el tutorial des del botó d'ajuda (?) en qualsevol moment."
        }
      ],
      skipDemo: 'Saltar demo',
      stepOf: 'de'
    },
    zh: {
      title: '让我们创建您的第一个设置！',
      subtitle: '按照这些步骤开始使用 Motivator',
      steps: [
        {
          icon: ListTodo,
          color: '#FF8BA7',
          title: '步骤 1：创建您的任务列表',
          description: '给您的第一个任务列表起个名字。例如："家务"、"工作"或"锻炼"。',
          placeholder: '例：我的日常任务',
          buttonText: '创建任务列表',
          creating: '创建中...'
        },
        {
          icon: Gift,
          color: '#FFC247',
          title: '步骤 2：创建您的奖励列表',
          description: '现在创建一个关联的奖励列表。在任务列表中获得的积分可以在这里兑换。',
          placeholder: '例：我的奖励',
          buttonText: '创建奖励列表',
          creating: '创建中...'
        },
        {
          icon: Link2,
          color: '#ADE498',
          title: '列表已关联！',
          description: '您的奖励列表已与任务列表关联。完成任务获得的积分只能兑换此列表中的奖励。',
          tip: '您可以创建多个任务列表，每个都有自己的奖励列表。'
        },
        {
          icon: Trophy,
          color: '#3B82F6',
          title: '一切就绪！',
          description: '您已经有了第一个设置。现在您可以添加任务、完成它们以获得积分，并兑换奖励。',
          buttonText: '开始使用 Motivator！',
          tip: '记住：您可以随时从帮助按钮（?）重新查看教程。'
        }
      ],
      skipDemo: '跳过演示',
      stepOf: '/'
    }
  };

  const c = content[lang] || content.es;
  const currentStep = c.steps[step];

  const handleCreateTaskList = async () => {
    if (!taskListName.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/task-lists`,
        { name: taskListName },
        { withCredentials: true }
      );
      setCreatedTaskList(response.data);
      setStep(1);
      toast.success('✓ Lista de tareas creada');
    } catch (error) {
      console.error('Error creating task list:', error);
      toast.error('Error al crear la lista');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRewardList = async () => {
    if (!rewardListName.trim() || !createdTaskList) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/reward-lists`,
        { 
          name: rewardListName,
          task_list_id: createdTaskList.list_id 
        },
        { withCredentials: true }
      );
      setCreatedRewardList(response.data);
      setStep(2);
      
      // Mini celebration
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 }
      });
    } catch (error) {
      console.error('Error creating reward list:', error);
      toast.error('Error al crear la lista');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    // Big celebration!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    
    setTimeout(() => {
      onComplete();
    }, 500);
  };

  const handleSkip = () => {
    onComplete();
  };

  const goToNextStep = () => {
    if (step < c.steps.length - 1) {
      setStep(step + 1);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="interactive-demo"
    >
      <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FF8BA7] to-[#FFC247] p-4 border-b-2 border-[#18181B] dark:border-zinc-600">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/90 border-2 border-[#18181B] flex items-center justify-center">
              <Rocket className="w-6 h-6 text-[#18181B]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#18181B]">{c.title}</h2>
              <p className="text-sm text-[#18181B]/70">{c.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-2">
            {c.steps.map((_, idx) => (
              <div 
                key={idx}
                className={`h-2 flex-1 rounded-full transition-all ${
                  idx < step ? 'bg-[#ADE498]' :
                  idx === step ? 'bg-[#FFC247]' :
                  'bg-zinc-200 dark:bg-zinc-600'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 text-right">
            {step + 1} {c.stepOf} {c.steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step Icon */}
          <div className="flex justify-center mb-4">
            <div 
              className="w-16 h-16 rounded-full border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center"
              style={{ backgroundColor: currentStep.color }}
            >
              <currentStep.icon className="w-8 h-8 text-[#18181B]" />
            </div>
          </div>

          {/* Step Title */}
          <h3 className="text-xl font-bold text-center mb-2 text-zinc-900 dark:text-white">{currentStep.title}</h3>
          <p className="text-zinc-600 dark:text-zinc-300 text-center mb-6">{currentStep.description}</p>

          {/* Step 0: Create Task List */}
          {step === 0 && (
            <div className="space-y-4">
              <input
                type="text"
                value={taskListName}
                onChange={(e) => setTaskListName(e.target.value)}
                placeholder={currentStep.placeholder}
                className="w-full h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 px-4 text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                data-testid="task-list-name-input"
                autoFocus
              />
              <button
                onClick={handleCreateTaskList}
                disabled={!taskListName.trim() || loading}
                className="w-full rounded-full border-2 border-[#18181B] bg-[#FF8BA7] text-[#18181B] font-bold py-3 hover:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="create-task-list-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {currentStep.creating}
                  </>
                ) : (
                  <>
                    {currentStep.buttonText}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 1: Create Reward List */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Show linked task list */}
              <div className="bg-[#FF8BA7]/20 dark:bg-[#FF8BA7]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl p-3 flex items-center gap-3">
                <ListTodo className="w-5 h-5 text-[#18181B] dark:text-[#FF8BA7]" />
                <div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Lista de tareas:</p>
                  <p className="font-bold text-zinc-900 dark:text-white">{createdTaskList?.name}</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-[#ADE498] ml-auto" />
              </div>

              <input
                type="text"
                value={rewardListName}
                onChange={(e) => setRewardListName(e.target.value)}
                placeholder={currentStep.placeholder}
                className="w-full h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 px-4 text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                data-testid="reward-list-name-input"
                autoFocus
              />
              <button
                onClick={handleCreateRewardList}
                disabled={!rewardListName.trim() || loading}
                className="w-full rounded-full border-2 border-[#18181B] bg-[#FFC247] text-[#18181B] font-bold py-3 hover:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="create-reward-list-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {currentStep.creating}
                  </>
                ) : (
                  <>
                    {currentStep.buttonText}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Linked explanation */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Show linked lists */}
              <div className="bg-zinc-50 border-2 border-[#18181B] rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF8BA7] border-2 border-[#18181B] flex items-center justify-center">
                    <ListTodo className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">{createdTaskList?.name}</p>
                    <p className="text-xs text-zinc-500">0 puntos</p>
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <Link2 className="w-5 h-5 text-[#ADE498]" />
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FFC247] border-2 border-[#18181B] flex items-center justify-center">
                    <Gift className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">{createdRewardList?.name}</p>
                    <p className="text-xs text-zinc-500">0 premios</p>
                  </div>
                </div>
              </div>

              {currentStep.tip && (
                <div className="bg-[#ADE498]/20 rounded-xl p-3 text-sm text-zinc-600 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#ADE498] flex-shrink-0 mt-0.5" />
                  <span>{currentStep.tip}</span>
                </div>
              )}

              <button
                onClick={goToNextStep}
                className="w-full rounded-full border-2 border-[#18181B] bg-[#ADE498] text-[#18181B] font-bold py-3 hover:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center justify-center gap-2"
              >
                Continuar
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step 3: All done */}
          {step === 3 && (
            <div className="space-y-4">
              {currentStep.tip && (
                <div className="bg-[#3B82F6]/10 rounded-xl p-3 text-sm text-zinc-600 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <span>{currentStep.tip}</span>
                </div>
              )}

              <button
                onClick={handleComplete}
                className="w-full rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-3 hover:bg-[#333] transition-all flex items-center justify-center gap-2"
                data-testid="finish-demo-btn"
              >
                <Sparkles className="w-5 h-5" />
                {currentStep.buttonText}
              </button>
            </div>
          )}
        </div>

        {/* Footer - Skip option (only on first two steps) */}
        {step < 2 && (
          <div className="px-6 pb-4">
            <button
              onClick={handleSkip}
              className="w-full text-center text-sm text-zinc-400 hover:text-zinc-600 py-2"
            >
              {c.skipDemo}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
